import { Box, Flex } from '@chakra-ui/react';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import { scaleOrdinal } from '@visx/scale';
import { useEffect, useRef } from 'react';
import { BaseLegend } from './base-legend';
import type { KeysPressOptions } from './legend';

interface Item {
  label: string; // identifier key
  displayLabel: string;
  color: string;
}

interface Props {
  items: Item[];
  selected?: string[] | null; // null/undefined means all selected
  onToggle?: (label: string, keys: KeysPressOptions) => void;
  legendHeight?: string;
  id?: string;
  description?: string;
}

export function ChipSampleLegend({ items, selected, onToggle, legendHeight, id }: Props) {
  const colorScale = scaleOrdinal<string, string>({
    domain: items.map(i => i.label),
    range: items.map(i => i.color),
  });

  const allSelected = !selected || selected.length === 0;

  const isSelected = (label: string) => (allSelected ? true : selected.includes(label));

  // Auto-scroll first selected sample/first item into view
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Ensure scroll logic runs only once after items are populated.
  const hasScrolledRef = useRef(false);

  // Scroll first selected sample into view on initial render only.
  useEffect(() => {
    if (hasScrolledRef.current) return;
    if (!items.length) return; // items not yet populated

    const container = containerRef.current;
    if (!container) return;

    const firstSelected = allSelected ? items[0]?.label : selected[0];
    if (!firstSelected) return;

    const idx = items.findIndex(it => it.label === firstSelected);
    if (idx < 0) return;

    const child = container.children[idx] as HTMLElement | undefined;
    child?.scrollIntoView({ block: 'start' });

    hasScrolledRef.current = true;
  }, [allSelected, items, selected]);

  return (
    <BaseLegend id={id} header="Tech Reps" description={'Toggle technical replicates'}>
      <Box
        maxHeight={legendHeight}
        overflowY="auto"
        overflowX="auto"
        className="download_legend-labels"
        ref={containerRef}
      >
        {items.map(item => (
          <Flex
            key={item.label}
            alignItems="center"
            mb="2px"
            pr={2}
            cursor={onToggle ? 'pointer' : undefined}
            opacity={isSelected(item.label) ? 1 : 0.4}
            style={{ userSelect: 'none' }}
            onClick={e => {
              if (!onToggle) return;
              const keys = getKeyPressesFromMouseClick(e);
              onToggle(item.label, keys);
            }}
          >
            <Box width="14px" height="14px" bg={colorScale(item.label)} borderRadius="2px" mr={2} />
            <Box flex="1" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" userSelect="none">
              {item.displayLabel}
            </Box>
          </Flex>
        ))}
      </Box>
    </BaseLegend>
  );
}
